import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useTRPCAPI, useTRPCClientAPI } from '../../../trpc/trpc';
import type { OnboardingStateAnswer } from '../../../../../music24-api/src/trpc/onboarding.router';

export interface FinishWelcomeOptions {
  /**
   * The SongsBrew tick box was ticked (design: songsbrew-promotion b1-b3). The field goes only
   * when it is true; an unticked box sends no input, as before.
   */
  subscribeSongsBrew?: boolean;
}

/**
 * Marks the welcome page as seen (`onboarding.finishWelcome`). A failure does not stop the user:
 * the page then shows again on the next sign-in, which does no harm. The server never fails the
 * welcome for a SongsBrew error.
 */
export function useFinishWelcome(): (options?: FinishWelcomeOptions) => Promise<void> {
  const trpcAPI = useTRPCAPI();
  const trpcClient = useTRPCClientAPI();
  const queryClient = useQueryClient();

  return useCallback(
    async (options?: FinishWelcomeOptions): Promise<void> => {
      try {
        await trpcClient.onboarding.finishWelcome.mutate(
          options?.subscribeSongsBrew === true ? { subscribeSongsBrew: true } : undefined
        );
      } catch (error) {
        console.error(error);
      }
      // Write the cache at once. The router opens the next page before the refetch ends, and
      // `useWelcomeRedirect` would send the person back to the welcome page.
      queryClient.setQueryData(trpcAPI.onboarding.state.queryKey(), (old: OnboardingStateAnswer | undefined) =>
        old === undefined ? old : { ...old, showWelcome: false }
      );
      queryClient.invalidateQueries({ queryKey: trpcAPI.onboarding.state.queryKey() }).catch(console.error);
      if (options?.subscribeSongsBrew === true) {
        // The server subscribed the person: Home and Settings must not show the old status.
        queryClient.invalidateQueries({ queryKey: trpcAPI.newsletter.getState.pathKey() }).catch(console.error);
      }
    },
    [queryClient, trpcAPI, trpcClient]
  );
}
